import Alert from '@material-ui/lab/Alert'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Grid from '@material-ui/core/Grid'
import HeadingCard from 'components/Card/HeadingCard'
import FormikTextField from 'components/Fields/FormikTextField'
import { Form, Formik, FormikHelpers } from 'formik'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { apiStatus } from 'shared/constants/AppEnum'
import { IApiState } from 'store/types'
import { ITempleResponse } from 'types/api/temple'
import { extractError, isFieldError } from 'utils/api'
import * as yup from 'yup'

export type PlatformSettingsFormType = {
    allowZeroAmountTransactions: boolean
    enableTbaBooking: boolean
    tbaUpfrontBookingPercentage: number
}

interface PlatformSettingsCardProps {
    temple: ITempleResponse
    updateApi: IApiState
    handleSubmit: (
        values: PlatformSettingsFormType,
        actions: FormikHelpers<PlatformSettingsFormType>
    ) => void
}

const getInitialValues = (temple: ITempleResponse): PlatformSettingsFormType => ({
    allowZeroAmountTransactions: temple.allowZeroAmountTransactions ?? false,
    enableTbaBooking: temple.enableTbaBooking ?? false,
    tbaUpfrontBookingPercentage: temple.tbaUpfrontBookingPercentage ?? 100,
})

const validationSchema = yup.object({
    allowZeroAmountTransactions: yup.boolean(),
    enableTbaBooking: yup.boolean(),
    tbaUpfrontBookingPercentage: yup
        .number()
        .typeError('TBA upfront percentage must be a number')
        .min(0, 'TBA upfront percentage cannot be negative')
        .max(100, 'TBA upfront percentage cannot be more than 100')
        .required('TBA upfront percentage is required'),
})

const PlatformSettingsCard: React.FC<PlatformSettingsCardProps> = ({
    temple,
    updateApi,
    handleSubmit,
}) => {
    const { messages } = useIntl()
    const initialValues = useMemo(() => getInitialValues(temple), [temple])

    return (
        <HeadingCard title='Platform Settings'>
            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ dirty, isSubmitting, isValid, setFieldValue, values }) => (
                    <Form noValidate autoComplete='off'>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Box style={{ display: 'flex', flexDirection: 'column' }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                onChange={(e) => {
                                                    setFieldValue(
                                                        'allowZeroAmountTransactions',
                                                        e.target.checked,
                                                    )
                                                }}
                                                checked={values.allowZeroAmountTransactions}
                                                color='primary'
                                            />
                                        }
                                        label='Allow zero amount financial receipts'
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                onChange={(e) => {
                                                    setFieldValue('enableTbaBooking', e.target.checked)
                                                }}
                                                checked={values.enableTbaBooking}
                                                color='primary'
                                            />
                                        }
                                        label='Enable TBA Booking'
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <FormikTextField
                                    name='tbaUpfrontBookingPercentage'
                                    variant='outlined'
                                    fullWidth
                                    size='small'
                                    type='number'
                                    inputProps={{ min: 0, max: 100, step: 'any' }}
                                    placeholder='TBA upfront percentage'
                                    label='TBA upfront percentage'
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    disabled={isSubmitting || !isValid || !dirty}
                                    variant='contained'
                                    color='primary'
                                    type='submit'
                                >
                                    {messages['common.save']}
                                </Button>
                            </Grid>
                            {updateApi.status === apiStatus.FAILED && !isFieldError(updateApi.error) && (
                                <Grid item xs={12}>
                                    <Alert severity='error'>{extractError(updateApi.error)}</Alert>
                                </Grid>
                            )}
                        </Grid>
                    </Form>
                )}
            </Formik>
        </HeadingCard>
    )
}

export default PlatformSettingsCard
